/* eslint-disable no-console */
/**
 * Contains implementations for SmartSolve functions (defined in public/static/js/pilgrim.expression.formulas.js)
 */
import _ from 'lodash';

import { evaluate, evaluateConditions, resolveData } from './evaluation.utils';
import {
  getCurrentGridIndex,
  getDataPath,
  getDataType,
  getEntityPrimaryKey,
  getFullDataSetByDataMember,
  toArray,
} from './form-manager.utils';
import { getGridArrIndexForRowFilter } from './runtime.utils';
import {
  getAddBusinessDays,
  getLicensedModule,
  getNumberingScheme,
} from '../../api/instances/smartsolve';
import { isTrue } from '../../utils/commonUtils';
import { ENTITY_DATA_PREFIX } from '../constants/form-manager.constants';

export const rowCountImpl = (ssContext, data, dataMember, templateName) => {
  const { formModel, isParentlessTable } = ssContext;
  let dataArr = toArray(_.get(data, dataMember));

  if (isParentlessTable) {
    // get data disregard of gridArrIndex
    dataArr = getFullDataSetByDataMember(ssContext.data, dataMember);
  }

  let rowAmount = dataArr.length;

  if (!templateName) {
    return rowAmount;
  }

  const control = Object.values(formModel).find(
    ({ uniqueIdentifier }) => uniqueIdentifier === templateName
  );

  if (_.isNil(control) || _.isNil(control.filter)) {
    return rowAmount;
  }

  const filters = control.filter;

  if (typeof filters === 'string' || typeof filters === 'boolean') {
    return isTrue(filters) ? rowAmount : 0;
  }

  const filterArray = dataArr.map((row, rowIndex) =>
    evaluateConditions({
      conditions: filters.Conditions,
      ssContext: {
        ...ssContext,
        gridArrIndex: getGridArrIndexForRowFilter(ssContext.gridArrIndex, rowIndex),
        useRowData: true,
      },
      datasourceRow: row,
    })
  );

  rowAmount = dataArr.filter(
    (row, i) => filterArray[i] || row.ContainerInstanceIdForHandMadeNewObject === control.controlId
  ).length;

  return rowAmount;
};

// legacy app equivalent for currentRecordId() / get_PrimaryKeyValue()
export const currentRecordIdImpl = (ssContext) => {
  const { data, entityType, entityDefinition } = ssContext;
  const topEntity = _.get(data, `${ENTITY_DATA_PREFIX}${entityType}`);

  if (_.isNil(topEntity)) {
    return null;
  }

  const cPrimaryKey = getEntityPrimaryKey(entityDefinition, entityType);

  // TODO: Check if we can remove the latter part (PK_ID)
  return topEntity[cPrimaryKey] || topEntity.PK_ID; // if table is support data without schema then use standard pkid
};

export const rowIndexImpl = (ssContext, dataRow) => {
  const UNDEFINED_ROW_INDEX = -1;

  if (dataRow == null) {
    // console.log(`ss.rowIndexImpl() : rowIndex not found for undefined dataRow`);
    return UNDEFINED_ROW_INDEX;
  }

  const { gridArrIndex } = ssContext;
  const rowIndex = getCurrentGridIndex(gridArrIndex);

  if (_.isNil(rowIndex)) {
    console.log('ss.rowIndexImpl() : rowIndex not found');
    return UNDEFINED_ROW_INDEX;
  }

  return rowIndex;
};

export const getCurrentRowValueImpl = (ssContext, dt, dataField, nullValue) => {
  const { contextDataMember = '', gridArrIndex, data, entityDefinition } = ssContext;

  const srcPath = getDataPath(contextDataMember, gridArrIndex, entityDefinition);
  const row = _.get(data?.entityDataStore?.entityData, srcPath, {});
  const rowValue = row[dataField];

  return rowValue || nullValue;
};

export const readValue = (value, dataType, useString) => {
  switch (dataType) {
    case 'System.Int32':
      return parseInt(value, 10);
    case 'System.Decimal':
      return parseFloat(value);
    case 'System.String':
      return value;
    case 'System.DateTime':
    case 'System.Byte':
    case 'System.Boolean':
      return +value;
    default:
      return useString ? value : parseFloat(value); // if data type is missed in entity definition (e.g. V_MASSI-V_MASSITODO) -> get number
  }
};

const sortNaturalArray = (mappedColumnValues) => {
  const currentCulture = localStorage.getItem('culture') || 'en-US';

  const collator = new Intl.Collator(currentCulture, {
    numeric: true,
    sensitivity: 'variant',
    caseFirst: 'lower',
  });

  return mappedColumnValues.sort(collator.compare);
};

export const getExtremumValue = ({ dataType, mappedColumnValues, isMin }) => {
  switch (dataType) {
    case 'System.String':
      return isMin
        ? sortNaturalArray(mappedColumnValues).at(0) ?? ''
        : sortNaturalArray(mappedColumnValues).at(-1) ?? '';
    case 'System.DateTime':
      if (mappedColumnValues.length === 0) {
        return null;
      }

      return new Date(isMin ? Math.min(...mappedColumnValues) : Math.max(...mappedColumnValues));
    case 'System.Byte':
    case 'System.Boolean':
      return Boolean(isMin ? Math.min(...mappedColumnValues) : Math.max(...mappedColumnValues));
    default:
      return isMin ? Math.min(...mappedColumnValues) : Math.max(...mappedColumnValues);
  }
};

// apply filter defined for the container (grid or repeating panel)
const applyContainerFilter = (filters, ssContext) => (row) => {
  if (_.isNil(filters)) {
    return true;
  }

  return Array.isArray(filters)
    ? evaluateConditions({
        conditions: filters,
        ssContext: { ...ssContext, useRowData: true },
        datasourceRow: row,
      })
    : filters; // if not array -> true | false
};

const parseJSONFilter = (filterStr) => (filterStr ? JSON.parse(filterStr) : undefined);

export const getArrayForColumn = (
  ssContext,
  data,
  childDataMember,
  columnName,
  templateName,
  filterStr
) => {
  const { formModel } = ssContext;

  if (templateName && _.isNil(formModel)) {
    console.log('formModel is required but missed in ssContext');
    return [];
  }

  const childDataArr = toArray(data[childDataMember]);

  const containerFilters = templateName
    ? Object.values(formModel).find(({ uniqueIdentifier }) => uniqueIdentifier === templateName)
        ?.filter?.Conditions
    : parseJSONFilter(filterStr);

  const arrayForNumericColumn = childDataArr
    .filter(applyContainerFilter(containerFilters, ssContext))
    .filter((row) => !_.isNil(row[columnName]));

  return arrayForNumericColumn;
};

/**
 * Helper for sum*(), min*(), max*()
 *
 * reads entities from data model,
 * applies filter
 * and return specified column as array
 *
 * @returns {[number]}
 */
const getArrayForDataColumn = ({
  ssContext,
  data,
  childDataMember,
  columnName,
  templateName,
  filterStr,
  useString,
  dataType,
}) => {
  const columnArray = getArrayForColumn(
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName,
    filterStr
  );

  const arrayForNumericColumn = columnArray.map((row) =>
    readValue(row[columnName], dataType, useString)
  );

  return arrayForNumericColumn;
};

export const sumImpl = (ssContext, data, childDataMember, columnName, templateName) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.sumImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const numColArr = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName,
    filterStr: null,
    dataType,
  });
  const sum = numColArr.reduce((acc, val) => acc + val, 0);

  return sum;
};

export const convertArrayToDoublePipeDelimitedStringImpl = (
  ssContext,
  data,
  childDataMember,
  columnName,
  templateName
) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.convertArrayToDoublePipeDelimitedStringImpl(): required parameter is missed ');
    return undefined;
  }

  const colArr = getArrayForColumn(
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName,
    null
  );

  const result =
    colArr.length > 0
      ? colArr.slice(1).reduce((acc, cur) => `${acc}||${cur[columnName]}`, colArr[0][columnName])
      : '';

  return result;
};

export const sumWithFilterImpl = (ssContext, data, childDataMember, columnName, filterStr) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.sumWithFilterImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const numColArr = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName: null,
    filterStr,
    dataType,
  });
  const sum = numColArr.reduce((acc, val) => acc + val, 0);

  return sum;
};

export const columnValueWithFilterImpl = (
  ssContext,
  data,
  childDataMember,
  columnName,
  filterStr
) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.columnValueWithFilterImpl(): required parameter is missed ');
    return undefined;
  }

  const colArr = getArrayForColumn(
    ssContext,
    Array.isArray(data) ? data[0] : data,
    childDataMember,
    columnName,
    null,
    filterStr
  );

  return colArr.length === 1 ? colArr[0][columnName] : undefined;
};

export const minImpl = (ssContext, data, childDataMember, columnName, templateName) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.minImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const mappedColumnValues = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName,
    filterStr: null,
    dataType,
  });

  return getExtremumValue({ dataType, mappedColumnValues, isMin: true });
};

export const minimumValueImpl = (ssContext, data, childDataMember, columnName, templateName) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.minimumValueImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const numColArr = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName,
    filterStr: null,
    useString: true,
    dataType,
  });
  const allNumbers = numColArr.every((value) => !isNaN(value));

  if (numColArr.length && allNumbers) {
    return Math.min(...numColArr);
  }

  return numColArr.reduce(
    (acc, value) =>
      acc === '' ? value.toString() : value.toString() < acc ? value.toString() : acc,
    ''
  );
};

export const minWithFilterImpl = (ssContext, data, childDataMember, columnName, filterStr) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.minWithFilterImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const mappedColumnValues = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName: null,
    filterStr,
    dataType,
  });

  return getExtremumValue({ dataType, mappedColumnValues, isMin: true });
};

export const maxImpl = (ssContext, data, childDataMember, columnName, templateName) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.maxImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const mappedColumnValues = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName,
    filterStr: null,
    dataType,
  });

  return getExtremumValue({ dataType, mappedColumnValues, isMin: false });
};

export const maxWithFilterImpl = (ssContext, data, childDataMember, columnName, filterStr) => {
  if (_.isNil(data) || _.isNil(childDataMember) || _.isNil(columnName)) {
    console.log(' ss.maxWithFilterImpl(): required parameter is missed ');
    return undefined;
  }

  const { entityType, entityDefinition } = ssContext;
  const dataType = getDataType(entityDefinition, entityType, childDataMember, columnName);

  const mappedColumnValues = getArrayForDataColumn({
    ssContext,
    data,
    childDataMember,
    columnName,
    templateName: null,
    filterStr,
    dataType,
  });

  return getExtremumValue({ dataType, mappedColumnValues, isMin: false });
};

export const columnValueNotUniqueImpl = (ssContext, currentRow, columnName) => {
  if (_.isNil(currentRow) || _.isNil(columnName)) {
    throw new Error('currentRow and columnName are required');
  }

  const { entityDefinition, data, gridArrIndex, formModel, contextDataMember } = ssContext;

  if (_.isNil(formModel)) {
    console.log('ss.columnValueNotUnique() formModel is required but missed in ssContext');
    return false; // soft break formModel is required ...
  }

  const path = getDataPath(contextDataMember, gridArrIndex, entityDefinition);
  const gridDataRows = _.get(data.entityDataStore?.entityData, path.slice(0, -1), []);
  const rowsWithValueAmount = gridDataRows.filter(
    (row) => row[columnName] === currentRow[columnName]
  ).length;

  return rowsWithValueAmount > 1;
};

export const rowCountWithFilterImpl = (ssContext, data, dataMember, filterStr) => {
  if (_.isNil(data) || _.isNil(dataMember)) {
    throw new Error('ss.rowCountWithFilter(): data and dataMember are required');
  }

  const dataArr = toArray(_.get(data, dataMember));
  const containerFilters = parseJSONFilter(filterStr);
  const rowAmount = dataArr.filter(
    applyContainerFilter(containerFilters, { ...ssContext, conditionDataMember: dataMember })
  ).length;

  return rowAmount;
};

export const getNumberingSchemeImpl = (ssContext, numberingSchemeCode, orgList) => {
  return getNumberingScheme(numberingSchemeCode, orgList, 'nextValue', ssContext.formKey);
};

export const getLicensedModuleImpl = (ssContext, moduleName) => {
  return getLicensedModule(moduleName, ssContext.formKey);
};

export const getDateAddBusinessDaysImpl = (ssContext, numberOfDaysToAdd, startDate, siteCode) => {
  const identifier = (numberOfDaysToAdd + startDate + siteCode)
    .replace(/ *\([^)]*\) */g, '')
    .replace(/[+: ]/g, '');

  return getAddBusinessDays(numberOfDaysToAdd, startDate, siteCode, ssContext.formKey, identifier);
};

const preprocessMaskedFormula = (formula) =>
  '`' + formula.replaceAll('_.', '${_.').replaceAll("')", "')}") + '`';

export const evaluateDynamicFormulaImpl = (ssContext, data, target) => {
  if (_.isNil(data) || _.isNil(target)) {
    return;
  }

  // prepare string for eval
  const unmaskedTarget = target.replaceAll('{#', '{|').replaceAll('#}', '|}');
  const formula = resolveData(
    data,
    ssContext.entityType,
    ssContext.gridArrIndex,
    ssContext.entityDefinition,
    unmaskedTarget
  );

  return evaluate(preprocessMaskedFormula(formula), ssContext);
};
